import React, { useState } from 'react'
import { LinearProgress, makeStyles, useIsMobile, Typography, Tooltip } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { formatCurrency, getCrews } from '@paintscout/util/builder'
import type { CalendarEvent } from 'paintscout'
import { CREW_COLORS } from '@paintscout/util'
import LockIcon from '@material-ui/icons/Lock'
import ProductionEventPopper from '../ProductionEventPopper'
import type { CrewPalette } from '../util'
import { getCrewPalette, getProjectTypeLabel } from '../util'
import { useProductionEventPermissions } from 'shared/hooks'

const useStyles = makeStyles<Theme, CrewPalette>((theme) => ({
  root: {
    position: 'relative',
    padding: theme.spacing(1.5, 2),
    paddingBottom: theme.spacing(1),
    backgroundColor: ({ surface }) => surface,
    borderRadius: theme.borderRadius.md,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ surfaceHover }) => surfaceHover
      }
    }
  },
  viewOnlyOverlay: {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: 'inherit',
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  },
  lockIcon: {
    width: 14,
    height: 14,
    color: ({ accent }) => accent,
    marginLeft: 4
  },
  desktopBody: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  },
  desktopMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0
  },
  desktopRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.25)
  },
  mobileTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  mobileMiddleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  title: {
    color: ({ accent }) => accent,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap'
  },
  subtitleText: {
    color: ({ accentSoft }) => accentSoft,
    fontSize: theme.typography.pxToRem(12)
  },
  dotSeparator: {
    color: ({ accent }) => accent,
    fontWeight: 900,
    margin: theme.spacing(0, 1),
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1
  },
  crewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.borderRadius.md,
    backgroundColor: ({ accentSoft }) => accentSoft,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 400,
    lineHeight: 1.4,
    flexShrink: 0
  },
  price: {
    color: ({ accent }) => accent,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14)
  },
  hours: {
    color: ({ accentSoft }) => accentSoft,
    fontSize: theme.typography.pxToRem(12)
  },
  progressRoot: {
    height: theme.typography.pxToRem(6),
    borderRadius: '8px',
    backgroundColor: ({ track }) => track
  },
  progressBar: {
    backgroundColor: ({ progressFill }) => progressFill,
    borderRadius: '8px'
  }
}))

interface DayEventProps {
  event: CalendarEvent
}

export function DayEvent(props: DayEventProps) {
  const { event } = props

  const { options } = useClientOptions()
  const mobile = useIsMobile({ xs: true })
  const crews = getCrews({ options, includeTrashed: true })
  const eventCrew = event.crew ? crews.find((crew) => crew._id === event.crew) : null
  const projectTypeLabel = getProjectTypeLabel(event.projectType, options)
  const crewLabel = eventCrew?.name || 'Unassigned'

  const palette = getCrewPalette(eventCrew?.color || CREW_COLORS[0][0])
  const classes = useStyles(palette)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { permissionToViewDetails } = useProductionEventPermissions(event)
  const isViewOnly = !permissionToViewDetails

  const priceText = formatCurrency({ options, value: event.deal?.value })
  const hoursText = event.deal?.hours ? `${event.deal.hours}h` : ''

  const renderSubtitle = () => {
    if (!projectTypeLabel && !event.address) return null
    return (
      <Typography className={classes.subtitleText} component="span">
        {projectTypeLabel && <span>{projectTypeLabel}</span>}
        {projectTypeLabel && event.address && <span className={classes.dotSeparator}>•</span>}
        {event.address && <span>{event.address}</span>}
      </Typography>
    )
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  return (
    <>
      <Tooltip hideIcon disableWrapper content={isViewOnly ? `You don't have permission to access this job.` : ''}>
        <div
          className={`${classes.root}${isViewOnly ? ` ${classes.viewOnlyOverlay}` : ''}`}
          style={{ cursor: isViewOnly ? 'default' : undefined }}
          onClick={isViewOnly ? undefined : handleClick}
        >
          {mobile ? (
            <>
              <div className={classes.mobileTopRow}>
                <Typography className={classes.title}>
                  {event?.deal?.name}
                  {isViewOnly && <LockIcon className={classes.lockIcon} />}
                </Typography>
                <span className={classes.crewBadge}>{crewLabel}</span>
              </div>
              <div className={classes.mobileMiddleRow}>
                <Typography className={classes.price}>{priceText}</Typography>
                {hoursText && <Typography className={classes.hours}>{hoursText}</Typography>}
              </div>
              {renderSubtitle()}
            </>
          ) : (
            <div className={classes.desktopBody}>
              <div className={classes.desktopMain}>
                <Typography className={classes.title}>
                  {event?.deal?.name}
                  {isViewOnly && <LockIcon className={classes.lockIcon} />}
                </Typography>
                <div className={classes.subtitle}>
                  <span className={classes.crewBadge}>{crewLabel}</span>
                  {renderSubtitle()}
                </div>
              </div>
              <div className={classes.desktopRight}>
                <Typography className={classes.price}>{priceText}</Typography>
                {hoursText && <Typography className={classes.hours}>{hoursText}</Typography>}
              </div>
            </div>
          )}
          <LinearProgress
            classes={{
              root: classes.progressRoot,
              barColorPrimary: classes.progressBar
            }}
            value={event.progress || 0}
            variant="determinate"
          />
        </div>
      </Tooltip>
      <ProductionEventPopper event={event} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} />
    </>
  )
}
